import { createSlice } from "@reduxjs/toolkit";
import {
  getInventoryItems,
  adjustStock,
  bulkAdjustStock,
  getTransactions,
  getLowStock,
  getAudits,
  createAudit,
  getAuditDetail,
  getAuditItems,
  updateAudit,
  deleteAudit,
  scanAuditItem,
  unscanAuditItem,
  getDiscrepancyReport,
  approveAudit,
  exportAuditReport,
  getInventoryFilterOptions,
  generateSKU,
  replaceOrderItem,
  getAuditSales,
  getVariantBagLocations,
  getReservedFromOfficeOrders,
} from "./inventoryActions";

const initialState = {
  loading: false,
  error: null,
  success: false,
  items: {},
  transactions: {},
  lowStockItems: {},
  audits: {},
  activeAudit: {},
  auditItems: {},
  discrepancyReport: {},
  auditSales: {},
  lastScannedItem: null,
  adjustSuccess: false,
  replaceSuccess: false,
  replaceResult: null,
  filterOptions: { categories: [], brands: [], menus: [] },
  bagLocations: {},
  bagLocationsLoading: false,
  reservedFromOfficeOrders: {},
  reservedFromOfficeOrdersLoading: false,
};

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    resetError: (state, action) => {
      if (action?.payload && state.error && typeof state.error === "object") {
        delete state.error[action.payload];
        if (Object.keys(state.error).length === 0) {
          state.error = null;
        }
      } else {
        state.error = null;
      }
      state.loading = false;
      state.success = false;
    },
    resetSuccess: (state) => {
      state.success = false;
      state.adjustSuccess = false;
      state.replaceSuccess = false;
      state.replaceResult = null;
    },
    resetLastScannedItem: (state) => {
      state.lastScannedItem = null;
    },
    resetBagLocations: (state) => {
      state.bagLocations = {};
      state.bagLocationsLoading = false;
    },
    resetReservedFromOfficeOrders: (state) => {
      state.reservedFromOfficeOrders = {};
      state.reservedFromOfficeOrdersLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // getInventoryItems
      .addCase(getInventoryItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInventoryItems.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.items = payload;
        state.error = null;
      })
      .addCase(getInventoryItems.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // adjustStock
      .addCase(adjustStock.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.adjustSuccess = false;
      })
      .addCase(adjustStock.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.adjustSuccess = true;
        state.error = null;
      })
      .addCase(adjustStock.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.adjustSuccess = false;
      })
      // bulkAdjustStock
      .addCase(bulkAdjustStock.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.adjustSuccess = false;
      })
      .addCase(bulkAdjustStock.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.adjustSuccess = true;
        state.error = null;
      })
      .addCase(bulkAdjustStock.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.adjustSuccess = false;
      })
      // getTransactions
      .addCase(getTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.transactions = payload;
        state.error = null;
      })
      .addCase(getTransactions.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // getLowStock
      .addCase(getLowStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLowStock.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.lowStockItems = payload;
        state.error = null;
      })
      .addCase(getLowStock.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // getAudits
      .addCase(getAudits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAudits.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.audits = payload;
        state.error = null;
      })
      .addCase(getAudits.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // createAudit
      .addCase(createAudit.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createAudit.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;
        state.audits = {
          meta: state?.audits?.meta,
          results: [payload, ...(state?.audits?.results || [])],
        };
        state.error = null;
      })
      .addCase(createAudit.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      })
      // getAuditDetail
      .addCase(getAuditDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAuditDetail.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeAudit = payload;
        state.error = null;
      })
      .addCase(getAuditDetail.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // getAuditItems
      .addCase(getAuditItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAuditItems.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.auditItems = payload;
        state.error = null;
      })
      .addCase(getAuditItems.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // updateAudit
      .addCase(updateAudit.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAudit.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeAudit = payload;
        state.success = true;
        state.error = null;
      })
      .addCase(updateAudit.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      })
      // deleteAudit
      .addCase(deleteAudit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAudit.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.error = null;
        if (Array.isArray(state.audits?.results)) {
          state.audits.results = state.audits.results.filter(
            (a) => a?.id !== payload
          );
        }
      })
      .addCase(deleteAudit.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // scanAuditItem
      .addCase(scanAuditItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(scanAuditItem.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.lastScannedItem = payload;
        state.error = null;
      })
      .addCase(scanAuditItem.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // unscanAuditItem
      .addCase(unscanAuditItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unscanAuditItem.fulfilled, (state) => {
        state.loading = false;
        state.lastScannedItem = null;
        state.error = null;
      })
      .addCase(unscanAuditItem.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // getDiscrepancyReport
      .addCase(getDiscrepancyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDiscrepancyReport.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.discrepancyReport = payload;
        state.error = null;
      })
      .addCase(getDiscrepancyReport.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // getAuditSales
      .addCase(getAuditSales.pending, (state) => {
        state.error = null;
      })
      .addCase(getAuditSales.fulfilled, (state, { payload }) => {
        state.auditSales = payload;
        state.error = null;
      })
      .addCase(getAuditSales.rejected, (state, { payload }) => {
        state.error = payload;
      })
      // approveAudit
      .addCase(approveAudit.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(approveAudit.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.activeAudit = payload;
        state.success = true;
        state.error = null;
      })
      .addCase(approveAudit.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      })
      // exportAuditReport
      .addCase(exportAuditReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportAuditReport.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(exportAuditReport.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // generateSKU
      .addCase(generateSKU.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateSKU.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(generateSKU.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // replaceOrderItem
      .addCase(replaceOrderItem.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.replaceSuccess = false;
        state.replaceResult = null;
      })
      .addCase(replaceOrderItem.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.replaceSuccess = true;
        state.replaceResult = payload;
        state.error = null;
      })
      .addCase(replaceOrderItem.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.replaceSuccess = false;
      })
      // getInventoryFilterOptions
      .addCase(getInventoryFilterOptions.fulfilled, (state, { payload }) => {
        state.filterOptions = payload;
      })
      // getVariantBagLocations
      .addCase(getVariantBagLocations.pending, (state) => {
        state.bagLocationsLoading = true;
        state.error = null;
      })
      .addCase(getVariantBagLocations.fulfilled, (state, { payload }) => {
        state.bagLocationsLoading = false;
        state.bagLocations = payload;
        state.error = null;
      })
      .addCase(getVariantBagLocations.rejected, (state, { payload }) => {
        state.bagLocationsLoading = false;
        state.error = payload;
      })
      // getReservedFromOfficeOrders
      .addCase(getReservedFromOfficeOrders.pending, (state) => {
        state.reservedFromOfficeOrdersLoading = true;
        state.error = null;
      })
      .addCase(getReservedFromOfficeOrders.fulfilled, (state, { payload }) => {
        state.reservedFromOfficeOrdersLoading = false;
        state.reservedFromOfficeOrders = payload;
        state.error = null;
      })
      .addCase(getReservedFromOfficeOrders.rejected, (state, { payload }) => {
        state.reservedFromOfficeOrdersLoading = false;
        state.error = payload;
      });
  },
});

export const {
  resetError,
  resetSuccess,
  resetLastScannedItem,
  resetBagLocations,
  resetReservedFromOfficeOrders,
} = inventorySlice.actions;
export default inventorySlice.reducer;
